import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/db/useAuth';
import { Loader2, LogIn } from 'lucide-react';
import type { FC, PropsWithChildren } from 'react';

/**
 * Signed-in gate only — authorization is {@link RequireRole}'s job, on the `user-roles` doc that
 * Firestore, Storage and the callables all read.
 *
 * This deliberately does NOT check the email domain. It used to, which made "works at Rock Home" a
 * second, weaker definition of admin: a staff address with no role got past this and then hit denied
 * reads everywhere, while a real admin signing in on another address was refused at the door. Anyone
 * without a role now lands on RequireRole's "Access Needed" card, which says what's actually wrong.
 */
const RequireLogin: FC<PropsWithChildren> = ({ children }) => {
    const { user, loading, error, signInWithGoogle } = useAuth();

    if (user) {
        return children;
    }

    return (
        <div className="flex h-screen items-center justify-center bg-gray-100 p-4">
            <Card className="w-[350px] max-w-full shadow-lg">
                <CardHeader className="text-center">
                    <CardTitle className="text-2xl font-semibold">Sign In</CardTitle>
                    <div className="text-gray-500 text-sm">Login with Google</div>
                </CardHeader>
                <CardContent className="flex flex-col gap-4">
                    <Button className="w-full flex gap-2" onClick={signInWithGoogle} disabled={loading}>
                        {loading ? (
                            <>
                                <Loader2 className="animate-spin h-5 w-5" /> Signing in...
                            </>
                        ) : (
                            <>
                                <LogIn className="h-5 w-5" /> Sign in with Google
                            </>
                        )}
                    </Button>
                    {error && <div className="text-destructive text-sm text-center">{error}</div>}
                </CardContent>
            </Card>
        </div>
    );
};

export default RequireLogin;
